// buildActivityFeedItems.js

/**
 * Pure helpers for the Node Activity v1 chronological feed.
 *
 * Merges top-level comments + system activity events into one sorted list.
 */

import { shouldIncludeActivityEvent } from 'node/activity/utils/placeholderTitleRename';
import {
  collectCommentAttachmentNodeKeys,
  shouldSuppressAttachmentRelationshipEvent,
} from 'node/activity/utils/commentAttachments';

export const ACTIVITY_FEED_ITEM_KIND = Object.freeze({
  COMMENT: 'comment',
  SYSTEM: 'system',
  COLLAPSE: 'collapse',
});

/**
 * @param {object|null|undefined} event
 * @returns {boolean}
 */
export function isWorkflowProgressDetailEvent(event) {
  if (!event) return false;
  if (String(event.prominence || '').trim() === 'detail') return true;
  return String(event.source || '').trim() === 'workflow_task_checklist';
}

/**
 * @param {object|null|undefined} event
 * @returns {string}
 */
export function getWorkflowProgressStepKey(event) {
  return String(event?.stepNodeKey || event?.raw?.stepNodeKey || '').trim();
}

/**
 * Collapse consecutive host-lifted check-offs on the same step.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function collapseWorkflowProgressDetails(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const next = [];
  let index = 0;

  while (index < list.length) {
    const row = list[index];
    const isDetail = row?.kind === ACTIVITY_FEED_ITEM_KIND.SYSTEM
      && isWorkflowProgressDetailEvent(row.event);
    const stepKey = isDetail ? getWorkflowProgressStepKey(row.event) : '';

    if (!isDetail || !stepKey) {
      next.push(row);
      index += 1;
      continue;
    }

    const run = [row];
    let cursor = index + 1;
    while (cursor < list.length) {
      const candidate = list[cursor];
      const sameStep = candidate?.kind === ACTIVITY_FEED_ITEM_KIND.SYSTEM
        && isWorkflowProgressDetailEvent(candidate.event)
        && getWorkflowProgressStepKey(candidate.event) === stepKey;
      if (!sameStep) break;
      run.push(candidate);
      cursor += 1;
    }

    if (run.length < 2) {
      next.push(row);
      index += 1;
      continue;
    }

    const firstEvent = run[0].event || {};
    next.push({
      kind: ACTIVITY_FEED_ITEM_KIND.COLLAPSE,
      key: `collapse:${run[0].key}`,
      at: run[0].at,
      count: run.length,
      stepTitle: String(firstEvent.stepTitle || '').trim(),
      events: run.map((entry) => entry.event),
      items: run,
    });
    index = cursor;
  }

  return next;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function toActivityTimestampMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

/**
 * @param {object} comment
 * @returns {boolean}
 */
export function isTopLevelComment(comment) {
  if (!comment) return false;
  const parentKey = comment.parentKey;
  return parentKey == null || parentKey === '';
}

/**
 * @param {object[]} comments
 * @param {{ includeDeleted?: boolean }} [options]
 * @returns {object[]}
 */
export function selectTopLevelComments(comments, { includeDeleted = true } = {}) {
  return (Array.isArray(comments) ? comments : []).filter((comment) => {
    if (!isTopLevelComment(comment)) return false;
    if (includeDeleted) return true;
    return !comment?.deleted;
  });
}

/**
 * Builds chronological feed rows from comments + system events.
 *
 * @param {object} params
 * @param {object[]} params.comments
 * @param {object[]} params.events
 * @param {string} [params.typeLabel]
 * @param {Iterable<string>} [params.draftAttachmentNodeKeys]
 * @returns {Array<object>}
 */
export function buildRawActivityRows({
  comments = [],
  events = [],
  typeLabel = '',
  draftAttachmentNodeKeys = null,
} = {}) {
  const rows = [];
  const attachmentNodeKeys = collectCommentAttachmentNodeKeys(
    comments,
    draftAttachmentNodeKeys
  );

  selectTopLevelComments(comments).forEach((comment) => {
    const key = String(comment?.key || '').trim();
    if (!key) return;
    rows.push({
      kind: ACTIVITY_FEED_ITEM_KIND.COMMENT,
      key: `comment:${key}`,
      at: toActivityTimestampMs(comment?.dateCreated),
      comment,
    });
  });

  (Array.isArray(events) ? events : []).forEach((event) => {
    if (!shouldIncludeActivityEvent(event, { typeLabel })) return;
    if (shouldSuppressAttachmentRelationshipEvent(event, attachmentNodeKeys)) return;
    const key = String(event?.key || event?.id || '').trim();
    if (!key) return;
    rows.push({
      kind: ACTIVITY_FEED_ITEM_KIND.SYSTEM,
      key: `system:${key}`,
      at: toActivityTimestampMs(event?.occurredAt)
        || toActivityTimestampMs(event?.timestamp)
        || toActivityTimestampMs(event?.dateCreated),
      event,
    });
  });

  rows.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    return String(a.key).localeCompare(String(b.key));
  });

  return rows;
}

/**
 * @param {object} params
 * @param {object[]} [params.comments]
 * @param {object[]} [params.events]
 * @param {string} [params.typeLabel]
 * @param {Iterable<string>} [params.draftAttachmentNodeKeys]
 * @returns {Array<object>}
 */
export function buildActivityFeedItems({
  comments = [],
  events = [],
  typeLabel = '',
  draftAttachmentNodeKeys = null,
  collapseDetails = false,
} = {}) {
  const rows = buildRawActivityRows({
    comments,
    events,
    typeLabel,
    draftAttachmentNodeKeys,
  });

  if (collapseDetails !== true) return rows;
  return collapseWorkflowProgressDetails(rows);
}
